import { formatCPF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";

export function DeleteInscricaoDialog({
  toDelete, confirmText, setConfirmText, deleting,
  onClose, onConfirm,
}) {
  return (
    <Dialog open={!!toDelete} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-delete-inscricao">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" /> Excluir Inscrição
          </DialogTitle>
          <DialogDescription>
            Esta ação é <strong>permanente</strong>. Um backup SQLite será salvo automaticamente
            em <code className="text-xs">backups/pre-delete/</code> antes da exclusão.
          </DialogDescription>
        </DialogHeader>
        {toDelete && (
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
              <div><strong>Protocolo:</strong> {toDelete.protocolo}</div>
              <div><strong>Nome:</strong> {toDelete.nome_completo}</div>
              <div><strong>CPF:</strong> {formatCPF(toDelete.cpf)}</div>
              <div><strong>Status:</strong> {toDelete.status}</div>
            </div>
            <div>
              <label className="text-sm font-semibold">Digite <code>EXCLUIR</code> para confirmar:</label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="EXCLUIR"
                autoFocus
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={deleting || confirmText !== "EXCLUIR"}
            data-testid="btn-confirm-delete"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {deleting ? "Excluindo..." : "Excluir definitivamente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDeleteInscricoesDialog({
  open, onOpenChange, selected, bulkPreview, bulkConfirm, setBulkConfirm,
  bulkDeleting, onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-bulk-delete">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" /> Excluir {selected.size} Inscrições
          </DialogTitle>
          <DialogDescription>
            Esta ação é <strong>permanente</strong>. Um único backup SQLite consolidado será salvo
            com todas as {selected.size} inscrições + seus audit logs antes da exclusão.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs max-h-40 overflow-y-auto">
            <strong>Serão excluídas:</strong>
            <ul className="mt-1 space-y-0.5">
              {bulkPreview.map((i) => (
                <li key={i.id} className="truncate">
                  <span className="font-mono">{i.protocolo}</span> — {i.nome_completo}
                </li>
              ))}
              {selected.size > 20 && (
                <li className="italic text-slate-600">...e mais {selected.size - 20} inscrições</li>
              )}
            </ul>
          </div>
          <div>
            <label className="text-sm font-semibold">Digite <code>EXCLUIR TUDO</code> para confirmar:</label>
            <Input
              value={bulkConfirm}
              onChange={(e) => setBulkConfirm(e.target.value)}
              placeholder="EXCLUIR TUDO"
              autoFocus
              data-testid="bulk-confirm-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={bulkDeleting}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={bulkDeleting || bulkConfirm !== "EXCLUIR TUDO"}
            data-testid="btn-confirm-bulk-delete"
          >
            {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {bulkDeleting ? "Excluindo..." : `Excluir ${selected.size} inscrições`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
